type PerfApiTiming = {
  method: string;
  path: string;
  status: number;
  durationMs: number;
  responseBytes?: number;
  serverTiming?: string | null;
  error?: string | null;
  startedAtMs?: number;
};

type ActivePageTrace = {
  traceId: string;
  pageLoadId: string;
  route: string;
  startedAtIso: string;
  startedAtMs: number;
  apiTimings: PerfApiTiming[];
  longTaskCount: number;
  flushTimer: number | null;
  lastFlushMs: number;
  loadSettledAtMs: number | null;
  loadSettledReason: string | null;
};

const MAX_API_TIMINGS = 80;
const API_IDLE_FLUSH_MS = 1200;
const INITIAL_FLUSH_MS = 3000;

let activeTrace: ActivePageTrace | null = null;
let longTaskObserverStarted = false;

function resolvePerfBaseUrl(): string {
  const env = (typeof import.meta !== 'undefined'
    ? (import.meta as { env?: Record<string, string | undefined> }).env
    : undefined);
  return env?.VITE_API_BASE_URL || '/api/v1';
}

function isPerfTraceEnabled(): boolean {
  const env = (typeof import.meta !== 'undefined'
    ? (import.meta as { env?: Record<string, string | undefined> }).env
    : undefined);
  if (env?.VITE_ENABLE_PERF_TRACE === 'true') return true;
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem('boox_perf_trace') === 'true';
}

function nowMs(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

function makeId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function readAuthToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return (
    localStorage.getItem('boox_token')
    || localStorage.getItem('access_token')
    || sessionStorage.getItem('boox_token')
    || null
  );
}

function estimateDomNodeCount(): number | null {
  if (typeof document === 'undefined') return null;
  return document.getElementsByTagName('*').length;
}

function getNavigationType(): string | null {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') return null;
  const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return entry?.type ?? null;
}

function startLongTaskObserver(): void {
  if (longTaskObserverStarted || typeof PerformanceObserver === 'undefined') return;
  try {
    const observer = new PerformanceObserver((list) => {
      if (!activeTrace) return;
      activeTrace.longTaskCount += list.getEntries().length;
    });
    observer.observe({ entryTypes: ['longtask'] });
    longTaskObserverStarted = true;
  } catch {
    longTaskObserverStarted = true;
  }
}

function buildPayload(trace: ActivePageTrace, reason: string, final: boolean) {
  const apiTimings = trace.apiTimings.slice(-MAX_API_TIMINGS);
  const apiDurations = trace.apiTimings.map((item) => item.durationMs);
  const apiErrorCount = trace.apiTimings.filter((item) => item.status >= 400 || item.status === 0).length;
  const endedAtMs = nowMs();
  const shouldSettleLoad = !final && (reason === 'initial-idle' || reason === 'api-idle' || reason === 'manual');
  if (shouldSettleLoad && trace.loadSettledAtMs === null) {
    trace.loadSettledAtMs = endedAtMs;
    trace.loadSettledReason = reason;
  }
  const loadEndedAtMs = trace.loadSettledAtMs ?? endedAtMs;

  return {
    traceId: trace.traceId,
    pageLoadId: trace.pageLoadId,
    route: trace.route,
    startedAt: trace.startedAtIso,
    endedAt: new Date().toISOString(),
    durationMs: Math.max(0, Math.round(loadEndedAtMs - trace.startedAtMs)),
    final,
    flushReason: reason,
    navigationType: getNavigationType(),
    visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    metrics: {
      apiCount: trace.apiTimings.length,
      apiErrorCount,
      apiTotalMs: Math.round(apiDurations.reduce((sum, item) => sum + item, 0)),
      apiMaxMs: Math.round(Math.max(0, ...apiDurations)),
      domNodeCount: estimateDomNodeCount(),
      longTaskCount: trace.longTaskCount,
    },
    apiTimings,
    traceWindowMs: Math.max(0, Math.round(endedAtMs - trace.startedAtMs)),
    loadSettledReason: trace.loadSettledReason ?? reason,
  };
}

function sendTracePayload(payload: ReturnType<typeof buildPayload>): void {
  if (!isPerfTraceEnabled()) return;
  if (typeof fetch === 'undefined') return;

  const token = readAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-boox-trace-id': payload.traceId,
    'x-boox-page-load-id': payload.pageLoadId,
    'x-boox-page-route': payload.route,
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  fetch(`${resolvePerfBaseUrl()}/perf/page-traces`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    // Performance logging must never affect product flows.
  });
}

function scheduleFlush(reason: string, delayMs: number): void {
  if (!activeTrace || typeof window === 'undefined') return;
  if (activeTrace.flushTimer !== null) {
    window.clearTimeout(activeTrace.flushTimer);
  }
  activeTrace.flushTimer = window.setTimeout(() => {
    flushPerfPageTrace(reason);
  }, delayMs);
}

export function beginPerfPageTrace(route: string): void {
  if (!isPerfTraceEnabled()) {
    activeTrace = null;
    return;
  }
  if (activeTrace) {
    finalizePerfPageTrace('route-replaced');
  }

  startLongTaskObserver();
  activeTrace = {
    traceId: makeId('trace'),
    pageLoadId: makeId('page'),
    route,
    startedAtIso: new Date().toISOString(),
    startedAtMs: nowMs(),
    apiTimings: [],
    longTaskCount: 0,
    flushTimer: null,
    lastFlushMs: 0,
    loadSettledAtMs: null,
    loadSettledReason: null,
  };
  scheduleFlush('initial-idle', INITIAL_FLUSH_MS);
}

export function flushPerfPageTrace(reason = 'manual'): void {
  if (!isPerfTraceEnabled()) return;
  if (!activeTrace) return;
  const trace = activeTrace;
  if (trace.flushTimer !== null && typeof window !== 'undefined') {
    window.clearTimeout(trace.flushTimer);
    trace.flushTimer = null;
  }

  const elapsedSinceLastFlush = nowMs() - trace.lastFlushMs;
  if (reason !== 'route-change' && reason !== 'pagehide' && elapsedSinceLastFlush < 500) return;

  trace.lastFlushMs = nowMs();
  sendTracePayload(buildPayload(trace, reason, false));
}

export function finalizePerfPageTrace(reason = 'route-change'): void {
  if (!isPerfTraceEnabled()) {
    activeTrace = null;
    return;
  }
  if (!activeTrace) return;
  const trace = activeTrace;
  activeTrace = null;

  if (trace.flushTimer !== null && typeof window !== 'undefined') {
    window.clearTimeout(trace.flushTimer);
  }

  sendTracePayload(buildPayload(trace, reason, true));
}

export function getPerfTraceHeaders(path?: string): Record<string, string> {
  if (!isPerfTraceEnabled()) return {};
  if (!activeTrace) return {};
  return {
    'x-boox-trace-id': activeTrace.traceId,
    'x-boox-page-load-id': activeTrace.pageLoadId,
    'x-boox-page-route': activeTrace.route,
  };
}

export function recordPerfApiTiming(timing: PerfApiTiming): void {
  if (!isPerfTraceEnabled()) return;
  if (!activeTrace) return;
  activeTrace.apiTimings.push(timing);
  if (activeTrace.apiTimings.length > MAX_API_TIMINGS * 2) {
    activeTrace.apiTimings = activeTrace.apiTimings.slice(-MAX_API_TIMINGS);
  }
  scheduleFlush('api-idle', API_IDLE_FLUSH_MS);
}
